import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Battery, Gauge, Zap, Clock, ShieldCheck, Palette, Ruler, Check, Download } from "lucide-react";
import { getProductBySlug, PRODUCTS } from "../data/products";
import { useTestRide } from "../components/TestRideContext";
import ProductCard from "../components/ProductCard";
import BrochureDialog from "../components/BrochureDialog";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  const { openModal } = useTestRide();
  const [active, setActive] = useState(0);
  const [variantIdx, setVariantIdx] = useState(0);
  const [brochureOpen, setBrochureOpen] = useState(false);

  if (!product) return <Navigate to="/products" replace />;

  const related = PRODUCTS.filter((p) => p.slug !== product.slug).slice(0, 3);
  const gallery = [product.image, ...(product.gallery || [])].filter(Boolean);
  const variants = product.variants || [];
  const activeVariant = variants[variantIdx];

  return (
    <div data-testid="product-detail-page" className="bg-white">
      <section className="relative pt-12 pb-20 border-b border-slate-200 overflow-hidden bg-gradient-to-b from-[#F0F6FF] to-white">
        <div className="absolute inset-0 helix-bg" />
        <div className="container-x relative">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-10 font-semibold" data-testid="back-to-products">
            <ArrowLeft size={16} /> All Models
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="relative aspect-square bg-gradient-to-br from-[#F6F8FB] via-white to-[#EEF3FA] border border-slate-200 rounded-3xl overflow-hidden">
                <div className="absolute inset-0 helix-bg opacity-60" />
                <img
                  src={gallery[active]}
                  alt={product.name}
                  className="absolute inset-0 w-full h-full object-contain p-8"
                />
                <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.22em] font-bold text-[#1E6FE5] border border-[#1E6FE5]/30 px-3 py-1.5 bg-white/80 backdrop-blur-sm rounded-full">
                  {product.categoryLabel}
                </div>
              </div>
              {gallery.length > 1 && (
                <div className="mt-3 grid grid-cols-5 gap-2" data-testid="product-gallery">
                  {gallery.map((g, i) => (
                    <button
                      key={i}
                      onClick={() => setActive(i)}
                      data-testid={`gallery-thumb-${i}`}
                      className={`aspect-square overflow-hidden border-2 rounded-xl bg-white ${active === i ? "border-[#2A8BFF]" : "border-slate-200 hover:border-slate-400"}`}
                    >
                      <img src={g} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
              <p className="eyebrow">{product.tagline}</p>
              <h1 className="font-outfit text-5xl lg:text-6xl xl:text-7xl font-bold mt-4 leading-[0.98] tracking-tighter text-slate-900">
                {product.name}
              </h1>
              <p className="text-slate-700 text-xl mt-5 leading-snug">{product.headline}</p>
              <p className="text-slate-600 mt-5 leading-relaxed">{product.description}</p>

              <div className="grid grid-cols-2 gap-3 mt-8">
                {[
                  { icon: <Gauge size={16} />, label: "Range", value: product.keySpecs.range },
                  { icon: <Zap size={16} />, label: "Top Speed", value: product.keySpecs.speed },
                  { icon: <Battery size={16} />, label: "Battery", value: product.keySpecs.battery },
                  { icon: <Clock size={16} />, label: "Motor", value: product.keySpecs.motor },
                ]
                  .filter((k) => k.value && k.value !== "—")
                  .map((k) => <KS key={k.label} icon={k.icon} label={k.label} value={k.value} />)}
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {product.badges.map((b) => (
                  <span key={b} className="text-[10px] uppercase tracking-[0.18em] font-bold border border-slate-200 px-3 py-1.5 text-slate-700 rounded-full bg-white">
                    {b}
                  </span>
                ))}
              </div>

              {variants.length > 0 && (
                <div className="mt-8 space-y-5" data-testid="variant-picker">
                  {variants.length > 1 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold flex items-center gap-2">
                        <Ruler size={12} /> Available options
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {variants.map((v, i) => (
                          <button
                            key={v.name}
                            onClick={() => setVariantIdx(i)}
                            data-testid={`variant-${i}`}
                            className={`text-[13px] font-bold px-4 py-2 border rounded-full transition-all ${
                              variantIdx === i
                                ? "border-slate-900 bg-slate-900 text-white"
                                : "border-slate-200 text-slate-700 hover:border-slate-400 bg-white"
                            }`}
                          >
                            {v.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeVariant?.colors?.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold flex items-center gap-2">
                        <Palette size={12} /> Colours
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {activeVariant.colors.map((c) => (
                          <span
                            key={c}
                            className="text-[12px] font-semibold border border-slate-200 rounded-full px-3.5 py-1.5 text-slate-700 bg-white"
                            data-testid={`color-${c}`}
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {product.warranty && (
                <div className="mt-6 flex items-center gap-3 border border-[#3FE0A6]/40 bg-[#3FE0A6]/8 p-4 rounded-2xl" data-testid="warranty-badge">
                  <span className="w-9 h-9 rounded-full bg-[#3FE0A6]/15 text-[#15B47E] inline-flex items-center justify-center shrink-0">
                    <ShieldCheck size={18} />
                  </span>
                  <p className="text-sm font-semibold text-slate-800">{product.warranty}</p>
                </div>
              )}

              <div className="mt-10 border-t border-slate-200 pt-6 flex flex-wrap items-end gap-6">
                <div className="flex gap-3 flex-wrap">
                  <button onClick={() => openModal({ model: product.name })} className="btn-primary" data-testid="detail-book-test-ride">
                    Book Test Ride
                  </button>
                  <Link to="/contact" className="btn-outline" data-testid="detail-inquire">
                    Enquire Now <ArrowUpRight size={16} />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setBrochureOpen(true)}
                    className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-slate-900 border-b border-slate-300 hover:border-slate-900 pb-1 transition"
                    data-testid="detail-download-brochure"
                  >
                    <Download size={14} /> Download brochure
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="section-padding bg-[#F6F8FB] border-b border-slate-200">
        <div className="container-x">
          <p className="eyebrow">Highlights</p>
          <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] max-w-3xl text-slate-900">
            Engineered for the modern rider.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {product.highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-[#2A8BFF]/40 hover:shadow transition"
              >
                <span className="text-[#1E6FE5] text-3xl font-outfit font-bold">0{i + 1}</span>
                <h3 className="font-outfit font-bold text-xl mt-4 text-slate-900">{h.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{h.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES (Cargo only) */}
      {product.useCases && product.useCases.length > 0 && (
        <section className="section-padding bg-slate-900 text-white" data-testid="use-cases">
          <div className="container-x">
            <div className="grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-6">
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#3FE0A6] font-bold">Perfect for</p>
                <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-white">
                  Move more, <br />
                  <span className="text-slate-400">every day.</span>
                </h2>
              </div>
              <div className="lg:col-span-6">
                <div className="grid sm:grid-cols-2 gap-3">
                  {product.useCases.map((u, i) => (
                    <motion.div
                      key={u}
                      initial={{ opacity: 0, y: 14 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.06 }}
                      className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-5 py-4 hover:bg-white/10 transition"
                    >
                      <span className="w-8 h-8 rounded-full bg-[#3FE0A6]/15 text-[#3FE0A6] inline-flex items-center justify-center shrink-0">
                        <Check size={16} strokeWidth={3} />
                      </span>
                      <p className="text-sm font-semibold text-white">{u}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SPECS */}
      <section className="section-padding bg-white">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="eyebrow">Full specs</p>
              <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">
                Built sheet, top to tyre.
              </h2>
              <p className="text-slate-600 mt-4 max-w-sm">
                {product.warranty
                  ? `${product.warranty} Service-ready at every eBikeGo store.`
                  : `Every ${product.name} is manufacturer-backed and service-ready at every eBikeGo store.`}
              </p>
              <div className="mt-8 flex items-center gap-3 border border-slate-200 p-5 bg-white rounded-2xl">
                <span className="w-12 h-12 rounded-full bg-[#3FE0A6]/15 text-[#15B47E] inline-flex items-center justify-center">
                  <ShieldCheck size={22} />
                </span>
                <div>
                  <p className="text-sm font-bold text-slate-900">Manufacturer-backed</p>
                  <p className="text-xs text-slate-500">Genuine parts • Store-led service</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="border border-slate-200 divide-y divide-slate-100 rounded-2xl overflow-hidden bg-white" data-testid="specs-table">
                {Object.entries(product.specs).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-2 gap-4 px-6 py-4 hover:bg-[#F6F8FB] transition">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">{k}</p>
                    <p className="text-sm text-slate-900 font-semibold">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="section-padding bg-[#F6F8FB] border-t border-slate-200">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <p className="eyebrow">Compare</p>
              <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">
                Other models you&apos;ll like.
              </h2>
            </div>
            <Link to="/products" className="btn-outline" data-testid="related-explore-all">
              See all <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      <BrochureDialog
        open={brochureOpen}
        onClose={() => setBrochureOpen(false)}
        productName={product.name}
      />
    </div>
  );
}

function KS({ icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
        <span className="text-[#1E6FE5]">{icon}</span> {label}
      </div>
      <p className="font-outfit text-xl font-bold mt-2 text-slate-900">{value}</p>
    </div>
  );
}
